import type { Account as GQLItem } from '../../../generated/internal.graphql';
import { take } from '../../../util/takePayload';
import * as AccountOwner from './AccountOwner';
import * as FeeStructure from './FeeStructure';
import * as SoundcheckLoan from './SoundcheckLoan';
import * as SoundcheckLoanLimits from './SoundcheckLoanLimits';

/**
 * A client account as the superadmin screens read and write it, on the internal (VP staff) schema.
 *
 * Named `Client` rather than `Account` because `Account.ts` in this directory is already the
 * schema-wide `AccountSummary` mapper — the same split AppSchema used before the move.
 *
 * Two fields AppSchema's `AccountDetails` carried are **not** here, because the internal `Account`
 * type does not expose them (sc-18758):
 * - `logo` — still *writable* via `ClientInput.logoId`, just no longer readable, so the edit screen
 *   cannot preview the current logo.
 * - `whitelabelConfig` — the object is gone; only the `whitelabelConfigId` scalar survives, which
 *   is all any screen ever used.
 */
export type ClientType = {
  id: number;
  name: string;
  cartLockDuration?: number;
  feeStructureId: number;
  feeStructure: FeeStructure.FeeStructure;
  onboarded?: boolean;
  owner?: AccountOwner.AccountOwner;
  soundcheckStripeAccountId?: string;
  whitelabelConfigId?: number;
  soundcheckLoan?: SoundcheckLoan.Loan;
  soundcheckLoanLimits?: SoundcheckLoanLimits.SoundcheckLoanLimits;
};

/**
 * Mirrors the internal `AccountAttributes` input. Note there is no `onboarded`: neither the internal
 * input nor the AppSchema one before it accepted it, and the backend's attribute slice drops it —
 * carrying it forward would keep claiming a write that never happened.
 */
export type ClientInput = {
  id?: number;
  name: string;
  cartLockDuration?: number;
  feeStructureId: number;
  logoId?: number;
  // Nullable where `logoId` is not: an absent key is dropped from the serialised variables, so
  // detaching a whitelabel config needs an explicit `null` rather than an omission.
  whitelabelConfigId?: number | null;
  soundcheckStripeAccountId?: string;
  owner?: {
    id?: number;
    name?: string;
    email?: string;
  };
};

export type ClientFilter = {
  name?: string;
};

export const fromGql = (item: GQLItem): ClientType => ({
  ...take<ClientType>(item, 'id', 'name', 'feeStructureId'),
  // Every optional field below is nullable on the internal schema, and `take` passes `null`
  // straight through — it only skips `undefined`. Map them one by one so no `null` leaks into a
  // `T | undefined` field.
  cartLockDuration: item.cartLockDuration ?? undefined,
  onboarded: item.onboarded ?? undefined,
  soundcheckStripeAccountId: item.soundcheckStripeAccountId ?? undefined,
  whitelabelConfigId: item.whitelabelConfigId ?? undefined,
  feeStructure: FeeStructure.fromGql(item.feeStructure),
  owner: item.owner ? AccountOwner.fromGql(item.owner) : undefined,
  soundcheckLoan: item.soundcheckLoan ? SoundcheckLoan.fromGql(item.soundcheckLoan) : undefined,
  soundcheckLoanLimits: item.soundcheckLoanLimits ? SoundcheckLoanLimits.fromGql(item.soundcheckLoanLimits) : undefined,
});

export const fromGqlCollection = (items: GQLItem[]): ClientType[] => items.map(fromGql);
